import { useState, useCallback } from 'react';
import { MedplumClient } from '@medplum/core';
import { Patient } from '@medplum/fhirtypes';
import { useDebounceSearch } from './useDebounceSearch';
import {
  getPatientsByName,
  getPatientNameSuggestions,
  getOrderIdSuggestions,
} from '../utils/fhir-helpers';
import { ORDER_SEARCH_DEFAULTS, OrderSearchParams } from '../orders/orderSearchConfig';

export function useOrderSearchForm(medplum: MedplumClient) {
  const [searchParams, setSearchParams] = useState<OrderSearchParams>(ORDER_SEARCH_DEFAULTS);

  const updateSearchParams = useCallback((updates: Partial<OrderSearchParams>) => {
    setSearchParams((prev) => ({ ...prev, ...updates }));
  }, []);

  const clearSearch = useCallback((defaults: OrderSearchParams) => {
    setSearchParams(defaults);
  }, []);

  const {
    query: _patientQuery,
    setQuery: setPatientQuery,
    loading: patientLoading,
  } = useDebounceSearch<Patient>({
    searchFunction: useCallback((q: string) => getPatientsByName(medplum, q), [medplum]),
    debounceDelay: 300,
    minQueryLength: 2,
    onError: (e) => console.error('Patient search error:', e),
  });

  const {
    query: patientNameQuery,
    setQuery: setPatientNameQuery,
    results: patientNameOptions,
    loading: patientNameLoading,
  } = useDebounceSearch<string>({
    searchFunction: useCallback((q: string) => getPatientNameSuggestions(medplum, q), [medplum]),
    debounceDelay: 300,
    minQueryLength: 2,
    onError: (e) => console.error('Patient name suggestion error:', e),
  });

  const {
    query: resourceIdQuery,
    setQuery: setResourceIdQuery,
    results: resourceIdOptions,
    loading: resourceIdLoading,
  } = useDebounceSearch<string>({
    searchFunction: useCallback((q: string) => getOrderIdSuggestions(medplum, q), [medplum]),
    debounceDelay: 300,
    minQueryLength: 2,
    onError: (e) => console.error('Order ID suggestion error:', e),
  });

  return {
    searchParams,
    updateSearchParams,
    clearSearch,
    patientNameQuery,
    setPatientNameQuery,
    setPatientQuery,
    patientNameOptions,
    patientLoading,
    patientNameLoading,
    resourceIdQuery,
    setResourceIdQuery,
    resourceIdOptions,
    resourceIdLoading,
  };
}
